import React from "react";
import type { ICascaderSearchResultCount } from "../../../../shared/utility/cascader-search";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { clearPickerCursor, KEYBOARD_NAV_ATTRIBUTE } from "./picker-popup-keyboard";

/**
 * Popup chrome for the cascader-based symbol picker: the KP Actions Panel header (search field),
 * the DOM host it renders into, the newest-column auto-scroll, the wheel hover guard, and the
 * search mirror. The popup has ONE presentation — Finder-style columns; the list/tree view and its
 * switch were removed (a running search already replaced either view with antd's flat result list).
 *
 * The visible search field lives here and remote-drives antd's hidden input; the
 * input must NOT be DOM-moved into the popup — React 17 only dispatches events for
 * nodes inside a container it instruments (pinned by
 * action-picker-search-reparent.test.tsx).
 */

/**
 * antd's `expandIcon` for both cascader pickers, pointing LEFT.
 *
 * The columns open right-to-left (`flex-direction: row-reverse` on the menus container in
 * styling.scss, which both pickers opt into via the `cascaderActionPicker` class), so a
 * right-pointing chevron sends the eye away from where the child column actually appears.
 * Mirrored as a path rather than a CSS rotate, so the glyph is not a flipped asset.
 *
 * Shared because it was duplicated: the same markup sat inline in both pickers, and mirroring one
 * of them left the other pointing the wrong way. `#999` is recoloured by
 * `.ant-cascader-menu-item-expand-icon svg path[fill] { fill: currentColor }`.
 */
export const PICKER_EXPAND_ICON: React.ReactElement = (
  <svg width="16" height="16" viewBox="0 0 16 16">
    <g fill="none" fillRule="evenodd">
      <path d="M0 0H16V16H0z"></path>
      <path
        fill="#999"
        d="M9.778 11.185L6.832 8.239l2.946-2.946c.296-.296.296-.775 0-1.07-.296-.297-.775-.297-1.07 0L5.222 7.707c-.296.296-.296.775 0 1.07l3.485 3.486c.296.296.775.296 1.07 0 .29-.296.297-.782 0-1.078z"
      ></path>
    </g>
  </svg>
);

export interface IPickerPopupChromeElements {
  header: HTMLElement;
}

// Creates (or finds) the header div inside the popup. It is a portal target; its content is
// rendered from the owning picker's render().
export function ensurePickerPopupChrome(popup: HTMLElement): IPickerPopupChromeElements {
  let header: HTMLElement | null = popup.querySelector(".kuika-action-picker-popup-header");
  if (!header) {
    header = document.createElement("div");
    header.className = "kuika-action-picker-popup-header";
    popup.insertBefore(header, popup.firstChild);
  }
  return { header };
}

// Finder-style columns in a fixed-width panel: reveal the newest column when a deeper
// level opens (antd appends menus, it never scrolls the container).
//
// The columns run RIGHT-TO-LEFT (`flex-direction: row-reverse`), so the newest one is the
// LEFTMOST and the reveal scrolls to the start of the strip, not its end. Assigning a large
// negative value rather than 0 because the setter clamps: that lands on the minimum scroll
// position whether the container reports its origin at the left edge (0, an LTR container) or
// to the left of it (negative) — no assumption about which convention is in play.
function createNewestColumnObserver(popup: HTMLElement): MutationObserver | null {
  const menusEl: HTMLElement | null = popup.querySelector(":scope > div:not([class])");
  if (!menusEl) return null;
  const observer: MutationObserver = new MutationObserver(() => {
    menusEl.scrollLeft = -menusEl.scrollWidth;
  });
  observer.observe(menusEl, { childList: true });
  return observer;
}

/** Quiet time after the last wheel event before hover may expand a row again. */
export const WHEEL_HOVER_IDLE_MS: number = 200;

/** Present on the popup while the wheel is turning; styling.scss drops row hit-testing under it. */
export const WHEELING_ATTRIBUTE: string = "data-wheeling";

// Both pickers expand on HOVER (antd `expandTrigger="hover"`, 150ms after mouseenter). Under a
// wheel the pointer stands still while the rows slide beneath it, so every row that rests there
// for 150ms fired a mouseenter and opened its own child column — the column to the left swapped
// again and again, and the scroll read as snagging on whichever row it passed (KOA-18639).
//
// While the wheel turns the popup carries WHEELING_ATTRIBUTE, and the stylesheet takes the rows
// out of hit-testing: the wheel lands on the column itself and keeps scrolling, no row receives
// mouseenter, and the one that was under the pointer gets its mouseleave, which cancels antd's
// pending expand. Once the wheel has been quiet for WHEEL_HOVER_IDLE_MS the attribute goes, the
// browser re-hit-tests the resting pointer, and the row under it expands as usual. An attribute
// rather than a class because antd rewrites the popup's className on every render.
function attachWheelHoverGuard(popup: HTMLElement): () => void {
  let idleTimer: number | null = null;
  const release = (): void => {
    idleTimer = null;
    popup.removeAttribute(WHEELING_ATTRIBUTE);
  };
  const onWheel = (): void => {
    popup.setAttribute(WHEELING_ATTRIBUTE, "");
    if (idleTimer !== null) window.clearTimeout(idleTimer);
    idleTimer = window.setTimeout(release, WHEEL_HOVER_IDLE_MS);
  };
  popup.addEventListener("wheel", onWheel, { passive: true });
  return (): void => {
    popup.removeEventListener("wheel", onWheel);
    if (idleTimer !== null) window.clearTimeout(idleTimer);
    release();
  };
}

// The pointer takes over from the keyboard on its first REAL movement: the cursor row goes and the
// rows hit-test again (KEYBOARD_NAV_ATTRIBUTE). Compared by coordinates because a browser may
// dispatch a synthetic `mousemove` at the resting position after a scroll — and the keyboard cursor
// scrolls rows into view — which must not throw the keyboard out of its own navigation.
function attachPointerTakeover(popup: HTMLElement): () => void {
  let lastX: number | null = null;
  let lastY: number | null = null;
  const onMouseMove = (event: MouseEvent): void => {
    const moved: boolean = event.clientX !== lastX || event.clientY !== lastY;
    lastX = event.clientX;
    lastY = event.clientY;
    if (moved && popup.hasAttribute(KEYBOARD_NAV_ATTRIBUTE)) {
      clearPickerCursor(popup);
    }
  };
  popup.addEventListener("mousemove", onMouseMove, { passive: true });
  return (): void => {
    popup.removeEventListener("mousemove", onMouseMove);
  };
}

export interface IPickerPopupBehaviourOptions {
  /**
   * Called after a pointer click inside the popup. rc-cascader focuses its own trigger on every row
   * select, which would leave the arrow keys to rc's internal navigation instead of the header
   * field's. Deferred a frame: this listener sits on the popup, below React's portal-root listener,
   * so it runs BEFORE rc's handler has moved focus away.
   */
  restoreFocus?: () => void;
}

export interface IPickerPopupBehaviours {
  disconnect: () => void;
}

// The popup's DOM-level behaviours — newest-column reveal, the wheel hover guard, the pointer
// takeover from the keyboard cursor and the focus return after a click — attached together once the
// popup exists and torn down together when it closes.
export function attachPickerPopupBehaviours(
  popup: HTMLElement,
  options: IPickerPopupBehaviourOptions = {}
): IPickerPopupBehaviours {
  const columnObserver: MutationObserver | null = createNewestColumnObserver(popup);
  const detachWheelGuard: () => void = attachWheelHoverGuard(popup);
  const detachPointerTakeover: () => void = attachPointerTakeover(popup);
  const restoreFocus: (() => void) | undefined = options.restoreFocus;
  const onClick = (): void => {
    if (restoreFocus) requestAnimationFrame(restoreFocus);
  };
  popup.addEventListener("click", onClick);
  return {
    disconnect: (): void => {
      columnObserver?.disconnect();
      detachWheelGuard();
      detachPointerTakeover();
      popup.removeEventListener("click", onClick);
      clearPickerCursor(popup);
    }
  };
}

// Remote-drives antd's hidden search input through the native value setter + a
// bubbling `input` event, keeping antd's whole search pipeline untouched.
export function mirrorSearchToInput(input: HTMLElement | null, text: string): void {
  if (!input) return;
  const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
  if (!nativeValueSetter) return;
  nativeValueSetter.call(input, text);
  input.dispatchEvent(new Event("input", { bubbles: true }));
}

export interface IPickerPopupHeaderProps {
  searchText: string;
  searchPlaceholder: string;
  /** Prefix for data-testids so co-existing pickers stay individually addressable. */
  testIdBase: string;
  /**
   * How many rows the running search lists, drawn inside the field. Omitted (or `null`) keeps the
   * plain field — which is what a picker that does not count its results passes.
   */
  resultCount?: ICascaderSearchResultCount | null;
  onSearchChange: (text: string) => void;
  /** Arrow keys / Enter / Escape from the field — the popup's keyboard navigation (picker-popup-keyboard.ts). */
  onSearchKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

// Popup header content — the search field, full width.
export const PickerPopupHeader: React.FC<IPickerPopupHeaderProps> = ({
  searchText,
  searchPlaceholder,
  testIdBase,
  resultCount,
  onSearchChange,
  onSearchKeyDown
}: IPickerPopupHeaderProps) => {
  // A zero is deliberately not drawn: PickerPopupEmptyState below already says "No Results Found",
  // and the design's no-results state (Figma 476:87561) shows the field without a badge.
  const countLabel: string | null =
    resultCount && resultCount.shown > 0
      ? `Search Results (${resultCount.shown}${resultCount.truncated ? "+" : ""})`
      : null;
  return (
    <div
      className={`kuika-action-picker-popup-search${countLabel ? " kuika-action-picker-popup-search--counted" : ""}`}
    >
      <input
        className="kuika-action-picker-popup-search__input"
        data-testid={`${testIdBase}PopupSearch`}
        placeholder={searchPlaceholder}
        value={searchText}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
        onKeyDown={onSearchKeyDown}
        autoFocus
      />
      {countLabel ? (
        <span className="kuika-action-picker-popup-search__count" data-testid={`${testIdBase}PopupSearchResultCount`}>
          {countLabel}
        </span>
      ) : null}
      <KuikaSvg className="kuika-action-picker-popup-search__icon" name="search" width={16} height={16} />
    </div>
  );
};

// "No results" tile shown as the cascader's notFoundContent.
export const PickerPopupEmptyState: React.FC = () => (
  <div className="kuika-action-picker-empty">
    <span className="kuika-action-picker-empty__icon">
      <KuikaSvg name="search" type="common" width={20} height={20} />
    </span>
    <span className="kuika-action-picker-empty__title">No Results Found</span>
  </div>
);
